/**
 * Utility and helper functions, not related to a specific module.
 *
 * @module core/util
 */

import * as App from './app';
import * as View from './view';
import _merge from 'lodash.merge';
import _mergeWith from 'lodash.mergewith';
import _cloneDeep from 'lodash.clonedeep';
import _debounce from 'lodash.debounce';

/**
 * Default util configuration.
 *
 * @since 3.3.1
 *
 * @type {object}
 *
 * @example
 * // default values
 * {
 *   id_index: [],
 *   alpha: 'abcdefghijklmnopqrstuvwxyz',
 * }
 */
export const config = {
  id_index: [],
  alpha: 'abcdefghijklmnopqrstuvwxyz',
};

/**
 * Remove false-y or undefined values (including empty arrays and objects) from an array.
 *
 * Returns a new array.
 *
 * @method
 *
 * @since 3.1.0
 *
 * @param {array} array - Array of values.
 * @param {boolean} strict - When enabled, only undefined values will be removed.
 *
 * @return {array}
 *
 * @example
 * let array = [0, 1, {}, 3, undefined, 5, []]
 *
 * // clean all falsey or empty
 * arrayClean(array);
 *
 * // expected result:
 * [1, 3, 5]
 *
 * // strict clean
 * arrayClean(array, true);
 *
 * // expected result:
 * [0, 1, {}, 3, 5, []]
 */
export const arrayClean = (array, strict = false) => strict ? array.filter(n => n !== undefined) : array.filter(n => !!n || (Array.isArray(n) && n.length) || (typeof n === 'object' && !Object.keys(n).length));

/**
 * Get only unique values from array. Returns an array of unique items.
 *
 * Note that array elements must be primitives that can be found with `Array.prototype.indexOf()`;
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} array - Array that may contain undesired duplicate elements.
 *
 * @return {array}
 *
 * @example
 * let array = [1, 11, 7, 7, 3, 7, 11, 35];
 *
 * arrayGetUnique(array);
 *
 * // expected result
 * [1, 11, 7, 3, 35]
 */
export const arrayGetUnique = (array) => array.filter((value, i, self) => self.indexOf(value) === i);

/**
 * Test if element is included in an array, including array or object values. If multiple elements are provided, the array must contain all of them.
 *
 * The javascript `Array.prototype.includes()` function does not succeed if the needle is an array or an object. When objects are provided, [`stringifyCompare`]{@link module:core/util.stringifyCompare} is used to compare search value and array element value.
 *
 * When primitives are provided, `Array.prototype.includes()` is used.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} array - Array that may contain mixed content types.
 * @param {...*} values - The values to search for.
 *
 * @returns {boolean}
 *
 * @example
 * // haystack array
 * let array = [ 'string', [1, 2], {c: 3, d: 4} ];
 *
 * // objects to find
 * let arrayValue = [1, 2];
 * let objectValue = {c: 3, d: 4};
 *
 * // typical array includes
 * array.includes('string'); // true
 * array.includes(arrayValue); // false
 * array.includes(objectValue); // false
 *
 * // this method
 * arrayIncludes(array, 'string'); // true
 * arrayIncludes(array, arrayValue); // true
 * arrayIncludes(array, objectValue); // true
 * arrayIncludes(array, 'string', arrayValue, objectValue); // true
 */
export const arrayIncludes = (array, ...values) => {
  // make sure our array is an array
  if (!Array.isArray(array)) throw `Util.arrayIncludes requires an array. Type provided: ${typeof array}`;

  // reduce all values to a single boolean match value
  return values.reduce((match, needle) => ((typeof needle === 'object') ? array.reduce((objMatch, element) => stringifyCompare(element, needle) ? true : objMatch, false) : array.includes(needle)) ? match : false, true);
};

/**
 * Add a value to array, without adding duplicates. Multiple values or an array of values may be added at a time using spread operator. Note that since [`arrayIncludes`]{@link module:core/util.arrayIncludes} is used, arrays or objects are acceptable values, in addition to primitives.
 *
 * Returns the number of items added to array.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} array - Destination array with unique values.
 *
 * @param {...*} values - Values to add to array.
 *
 * @return {number}
 *
 * @example
 * let array = [1, 7, 3, 11];
 *
 * let values = [1, 4, 7, 35];
 *
 * arrayPushUnique(array, ...values);
 *
 * // returned value:
 * 2
 *
 * // updated array value
 * [1, 7, 3, 11, 4, 35]
 */
export const arrayPushUnique = (array, ...values) => {
  // make sure array is an array
  if (!Array.isArray(array)) throw 'Util.arrayPushUnique() requires an array to add values to.';
  // get starting array length
  let startLength = array.length;
  // if each value is not found in array, push value to array
  for (let value of values) {
    if (!arrayIncludes(array, value)) array.push(value);
  }
  // return difference in start and end array lengths
  return array.length - startLength;
};

/**
 * Calculate "distances" from request values compared to a current product attribute value. Distances returned will be an array of all available distances based on the array of values requested, with smaller distances meaning a product is closer to being an exact match for a request value, and larger distances meaning less of a match. Distance values should be normalized to the following general paradigm:
 *
 * - 0 - exact match
 * - 1 - next closest match
 * - 2 - 2 steps away
 * - n... etc.
 *
 * ### Distances calculation
 *
 * Distance is calculated differently depending on the type of attribute and the whether the distance should be calculated linearly or logarithmically. Distances for each attribute type are calculated as follows:
 *
 * #### `boolean`
 * - If product value is equal to request value, set distance to 0, otherwise 1
 * - Note that string types with no provided `values` are treated as boolean
 *
 * #### `string`
 * - Find index of both product and request attribute value in list of `values`
 * - If either of the values are not found, skip
 * - Distance is equal to request index minus product index
 *
 * #### `number`
 * - if using linear scale, subtract request value from product value and normalize using `ln` argument value
 * - if using log scale, get base 2 log of ratio between request and product values
 *     - if product value >= request value, request is denominator
 *     - if request value > product value, product is denominator
 * - round result to a half integer (0, 0.5, 1, 1.5...)
 *
 * @method
 *
 * @param {string} attribute - Attribute name.
 *
 * @param {*} r - Current request values.
 *
 * @param {*} p - Current product attribute value.
 *
 * @param {object} attrDef - App [`attributes`]{@link module:core/app.attributes} definition for the current attribute type.
 *
 * @param {boolean} [reduce] - When enabled, distances array will be reduced to the single lowest value and returned. Otherwise, an array of all distances found will be returned. Default: `true`
 *
 * @param {array} [distances] - Array of distances. Since this function can be called recursively for nested array request values, this array is used to contain all relevant distance values.
 *
 * @returns {array|number}
 *
 * @see [`App.attributeMatch`]{@link module:core/app.attributeMatch}
 *
 * @example
 * // example of string-type attribute distances using a defined values order in attribute filter schema. note that actual distance values will vary depending on filter configuration.
 *
 * // request value
 * let r = ['i7', 'i5', 'i9'];
 * // product value
 * let p = 'i7';
 * // attribute type definition
 * let attrDef = {
 *   type: 'filter',
 *   allowMultiple: true,
 * }
 *
 * calcAttrDistances('processor', r, p, attrDef, false);
 * // expected result (not reduced): [0, -1, 1];
 * calcAttrDistances('processor', r, p, attrDef);
 * // expected result (reduced): 0;
 *
 * @example
 * // example of number-type attribute using log base 2 comparison. note that if the value fell between the highest and lowest request values it would be considered an exact match (distance 0)
 *
 * // request value
 * let r = [16, 32, 64];
 * // product value
 * let p = 4;
 * // attribute type definition
 * let attrDef = {
 *   type: 'filter',
 *   allowMultiple: true,
 * }
 *
 * calcAttrDistances('ram', r, p, attrDef, false);
 * // expected result (not reduced): [2, 3, 4];
 * calcAttrDistances('ram', r, p, attrDef);
 * // expected result (reduced): 2;
 */
export const calcAttrDistances = (attribute, r, p, attrDef, reduce = true, distances = []) => {
  // get schema definition for attribute. if no def, return empty array
  let def = App.settings[attrDef.type]?.schema?.[attribute];
  if (!def) return [];
  // if exact attribute match is found, return 0 distance
  if (App.attributeMatch(attribute, r, p, attrDef)) return reduce ? 0 : [0];
  // set up variables
  let w = def.weights || {};
  let l = w.linear || def.type === 'string' || false;
  let ln = w.linear_normalize || App.settings.filter.weights.linear_normalize;
  let lb = w.log_base || App.settings.filter.weights.log_base;
  let defaultOrder = def.values ? 'ASC' : def.type === 'number' ? 'DESC' : 'ASC';
  let asc = (def.order ?? defaultOrder) === 'ASC';
  // if request value is not an array, make it one
  if (!Array.isArray(r)) r = [r];
  // calculate distance for each value of request array
  for (let rv of r) {
    if (Array.isArray(rv)) {
      // recursively test array values
      calcAttrDistances(attribute, rv, p, attrDef, reduce, distances);
      continue;
    }
    let distance;
    // boolean, or string with no values
    if (def.type === 'boolean' || (def.type === 'string' && !def.values)) distance = p === rv ? 0 : 1;
    // string with values
    else if (def.type === 'string') {
      let pi = indexOfObjectByProperty('value', p, def.values);
      let ri = indexOfObjectByProperty('value', rv, def.values);
      // skip if request or product value not found
      if (pi === -1 || ri === -1) continue;
      distance = ri - pi;
    }
    // number
    else if (def.type === 'number') distance = l ? (rv - p) / ln : round(((p >= rv) ? -logBase(lb, p / rv) : logBase(lb, rv / p)) * 2) / 2;
    // if sort order is not ascending, flip distance sign
    if (!asc) distance *= -1;
    // push to distances
    distances.push(round(distance, 1));
  }
  // if no distances were found, push distance of 1
  if (!distances.length) distances.push(1);
  // return distances array or reduce to smallest distance
  return reduce ? distances.reduce((a, b) => Math.abs(b) < Math.abs(a) ? b : a) : distances;
};

/**
 * Find the Cartesian product of a series of arrays. Adapted from [SO]{@link https://stackoverflow.com/questions/12303989/cartesian-product-of-multiple-arrays-in-javascript}.
 *
 * Note that if only one value array is provided, it will be returned unchanged, except wrapped in an additional array.
 *
 * @method
 *
 * @since 3.2.7
 *
 * @param {...array} arrays - Arrays which will be used to generate a Cartesian product.
 *
 * @return {array}
 *
 * @example
 * let processors = ['i3', 'i5', 'i7'];
 * let storage = [500, 1000];
 * let ram = [16, 32];
 *
 * let product = cartesianProduct(processors, storage, ram);
 *
 * // expected output
 * [
 *   ['i3', 500, 16],
 *   ['i3', 500, 32],
 *   ['i3', 1000, 16],
 *   ['i3', 1000, 32],
 *   ['i5', 500, 16],
 *   ['i5', 500, 32],
 *   ['i5', 1000, 16],
 *   ['i5', 1000, 32],
 *   ['i7', 500, 16],
 *   ['i7', 500, 32],
 *   ['i7', 1000, 16],
 *   ['i7', 1000, 32],
 * ]
 */
export const cartesianProduct = (...arrays) => (arrays.length === 1) ? arrays : arrays.reduce((acc, array) => acc.flatMap(c => array.map(d => [c, d].flat())));

/**
 * For a given object, each array property value is [cleaned]{@link module:core/util.arrayClean}.
 *
 * Returns a new object.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {object} object - The object to clean.
 *
 * @param {boolean} strict - When enabled, only undefined values will be removed.
 *
 * @return {object}
 *
 * @see [`arrayClean`]{@link module:core/util.arrayClean}
 *
 * @example
 * let obj = {
 *   prop1: [0, 1, 2, undefined, 4],
 *   prop2: [false, '', [], 'true'],
 * };
 *
 * // clean all falsey
 * cleanObjectArrays(obj);
 *
 * // expected result:
 * {
 *   prop1: [1, 2, 4],
 *   prop2: ['true'],
 * }
 *
 * // strict clean (only undefined)
 * cleanObjectArrays(obj, true);
 *
 * // expected result:
 * {
 *   prop1: [0, 1, 2, 4],
 *   prop2: [false, '', [], 'true'],
 * }
 */
export const cleanObjectArrays = (object, strict = false) => {
  // if no object is supplied, just return it
  if (Array.isArray(object) || typeof object !== object) return object;
  // create object to hold cleaned values
  let cleaned = {};
  // for each property, if the value is an array, clean it
  for (let [property, value] of Object.entries(object)) cleaned[property] = Array.isArray(value) ? arrayClean(value, strict) : value;
  // return cleaned properties
  return cleaned;
};

/**
 * Deep clone an object using [lodash's cloneDeep]{@link https://lodash.com/docs/#cloneDeep}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} object - The object to clone.
 *
 * @return {object}
 */
export const cloneDeep = (object) => {
  return _cloneDeep(object);
};

/**
 * Condense array of [`attributes`]{@link module:core/config.attributes} objects into an object.
 *
 * Attribute object properties are attribute `name` values, and the property values are attribute `value` values. If no value is provided, a boolean `true` will be stored as the value.
 *
 * If there are multiple attribute objects with the same `name`, they will be either combined into an array, or saved as a single value, with the higher `priority` item taking precedence (if provided). Whether values are saved as an array or a single value depends on the `args.allowMultiple` value, which is typically defined in the [attribute's definition]{@link module:core/app.attributes}.
 *
 * If a `block` value is included in an attribute, and `allowMultiple` is not enabled in the attribute definition, the attribute value will be placed into an array at the index equal to the block value. This allows complex attribute objects to be constructed.
 *
 * *Notes:*
 * - *The presence of a priority in any attribute object will force a single item to be saved for values of that `name`, even if `args.allowMultiple` is enabled.*
 * - *This method only supports single-level complex attributes at this time (one child property).*
 * - Attribute definitions with a `rename` value provided will be stored as the new value in condensed attributes.
 *
 * @method
 *
 * @param {array} attributes - Array of attributes objects.
 *
 * @param {object} [args] - Attribute type definition.
 *
 * @param {string} [args.type] - Name of attribute type.
 *
 * @param {boolean} [args.allowMultiple] - When enabled, values from attributes with the same `name` will be stored as an array of items, rather than a single item. This setting is ignored if attribute `priority` is set.
 *
 * @param {string} [valueProp] - Attribute property to use as the condensed attribute value. Default: `value`
 *
 * @param {boolean} [meta] - When enabled, attribute meta will be returned instead of condensed attributes. Default: `false`
 *
 * @return {object}
 *
 * @example
 * let attrDef = {
 *   type: 'filter',
 *   allowMultiple: true,
 * };
 *
 * let attributes = [
 *   {
 *     name: 'processor',
 *     value: 'i3',
 *     priority: 5,
 *   },
 *   {
 *     name: 'processor',
 *     value: 'i7',
 *     priority: 3,
 *   },
 *   {
 *     name: 'os',
 *     value: 'windows',
 *   },
 *   {
 *     name: 'storage_amount',
 *     value: 500,
 *   },
 *   {
 *     name: 'touchscreen',
 *   },
 * ]
 *
 * condenseAttributes(attributes);
 *
 * // expected result:
 * {
 *   processor: ['i7'],
 *   os: ['windows'],
 *   storage_amount: [500],
 *   touchscreen: [true],
 * }
 */
export const condenseAttributes = (attributes, args = {}, valueProp = 'value', meta = false) => {
  // hold finalized attributes
  let attr = {};
  let priority = {};

  // set default value for attributes with no value provided
  let val = (valueProp === 'label') ? '' : true;

  for (let attribute of attributes) {
    // name of current attribute
    let name = attribute.name;
    // check if name is mapped to a new name
    let rename = getSchemaValue('rename', name, args);
    if (rename) name = rename;
    // get attribute value. if no value property is present, store as default value
    let value = attribute.hasOwnProperty(valueProp) ? attribute[valueProp] : val;
    // repeater block number, if provided
    let block = attribute.hasOwnProperty('block') ? attribute.block : false;
    // compare type, if provided
    let compare = attribute.compare || false;
    // set value to array if allowMultiple is enabled, using OR compare, or attribute is from a repeater block
    if (args.allowMultiple || compare === 'or' || block !== false) value = [value];

    // get priority for this attribute name, if it exists
    let existingPriority = priority[name] || false;
    // determine if the current attribute should be stored based on priority
    // note that higher priority means numerical value is smaller (i.e. 1 is highest priority)
    let attrHasPriority = attribute.priority && (!existingPriority || (existingPriority && existingPriority >= attribute.priority));

    // if a prioritized attribute is not the current highest priority, skip it
    if (attribute.priority && !attrHasPriority) continue;

    // store prioritized attributes
    if (attrHasPriority) {
      // store value
      attr[name] = value;
      // store attribute priority
      priority[name] = attribute.priority;
      // skip additional processing
      continue;
    }

    // store non-prioritized attributes
    // handle repeater block values
    if (block !== false) {
      // initialize array if not already
      if (!Array.isArray(attr[name])) attr[name] = [];
      // insert value at index equal to the block value
      attr[name][block] = value[0];
    }
    // if multiple values are allowed, and an array exists, add value to array
    else if ((args.allowMultiple || compare === 'or') && Array.isArray(attr[name])) arrayPushUnique(attr[name], ...value);
    // if AND compare, and both attribute are strings, concatenate them
    else if (compare === 'and' && typeof value === 'string' && typeof attr[name] === 'string') attr[name] = `${attr[name]} ${value}`;
    // otherwise, just save value. if multiple attributes have the same name, only the last value in the path will be saved
    // NOTE this case also handles creating the attribute array for any failed check above!
    else attr[name] = value;
  }

  // if meta is requested, return meta obj
  if (meta) return {priority};

  // format attribute values into their correct types
  if (valueProp === 'value') attr = formatAttributes(attr, args);

  // if allowMultiple is enabled, clean any value arrays before returning
  // if not enabled, create objects for any complex attributes
  return args.allowMultiple ? cleanObjectArrays(attr, true) : createComplexAttrObjects(attr, args);
};

/**
 * Count and return the number of decimals in a value. If value is not a number, 0 is returned.
 *
 * @method
 *
 * @param {number} value - Number to test.
 *
 * @return {number}
 *
 * @example
 * countDecimals(3) // 0
 * countDecimals(3.1) // 1
 * countDecimals(3.14) // 2
 */
export const countDecimals = (value) => (typeof value !== 'number') ? 0 : Number.isInteger(value) ? 0 : value.toString().split(".")[1].length || 0;

/**
 * Create objects out of individual complex attribute terms, when included in a properly structured attr object.
 *
 * The complex attribute object is created out of any consecutive properties within the complex attribute "family", starting with the root attribute term. Each term is assumed to have an array of values, the order of which determine their association with other attributes in the family.
 *
 * *Note that this method only supports single-level complex attributes at this time (one child property).*
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {object} attrObject - The attribute object to format.
 *
 * @param {object} [args] - Attribute type definition.
 *
 * @return {object}
 *
 * @example
 * // create object from example attr object
 * createComplexAttrObjects({
 *   ports: ['hdmi', 'thunderbolt_4'],
 *   ports:count: [1, 3],
 * });
 *
 * // expected result:
 * {
 *   ports: {
 *     hdmi: 1,
 *     thunderbolt_4: 3,
 *   },
 * }
 */
export const createComplexAttrObjects = (attrObject, args) => {
  // check for "complex" attributes that are provided directly as a path
  for (let name of Object.keys(attrObject)) {
    // check if name includes a path separator
    if (name.includes('.')) {
      // put value into attribute object
      putObjectValue(attrObject[name], attrObject, name, '.');
      // remove full attribute name
      delete attrObject[name];
    }
  }

  // format objects
  // gather complex attribute roots
  let roots = Object.keys(attrObject).filter(name => isAttrComplexRoot(name, args));

  // clean up any complex attributes that don't have root values
  let orphans = Object.keys(attrObject).filter(name => isAttrComplex(name) && !roots.includes(name.split(':')[0]));
  for (let orphan of orphans) {
    attrObject[orphan] = arrayClean(attrObject[orphan]);
  }

  // if there are no root terms, return
  if (!roots.length) return attrObject;

  // build complex objects
  for (let root of roots) {
    // if root term is not an array of values, skip it
    if (!Array.isArray(attrObject[root])) continue;
    // gather all available attributes in this complex attribute family, in order of length
    // additionally, only terms in consecutive order are kept
    let family = Object.keys(attrObject).filter(name => name.split(':')[0] === root).sort((a, b) => a.length - b.length).filter((name, i) => i === name.split(':').length - 1);
    // generate array of paths from family term values
    let paths = attrObject[root].map((value, i) => family.reduce((a, term) => a.length && attrObject[term][i] ? a.concat([attrObject[term][i]]) : [], [root]));
    // merge in path objects
    merge(attrObject, ...arrayClean(paths).map(path => putObjectValue(path.pop(), {}, path)));
    // delete non-root terms
    family.shift();
    for (let term of family) delete attrObject[term];
  }

  return attrObject;
};

/**
 * Debounce function execution. Uses [lodash's debounce]{@link https://lodash.com/docs/#debounce}.
 *
 * @method
 *
 * @since 3.2.3
 *
 * @param {function} func - The function to debounce.
 * @param {number} [wait] - The number of milliseconds to delay.
 * @param {object} [options]
 * @param {boolean} [options.leading] - Specify invoking on the leading edge of the timeout. Default: `false`
 * @param {number} [options.maxWait] - The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing] - Specify invoking on the trailing edge of the timeout. Default: `true`
 *
 * @return {function}
 */
export const debounce = (func, wait = 0, options = {}) => {
  return _debounce(func, wait, options);
};

/**
 * Decode Base64 string into array of string components. String components are the result of splitting the decoded string with "+".
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} value - String(s) to encode into Base64.
 *
 * @return {array}
 *
 * @example
 * decode('dmlld19pZCt0ZXN0IHN0cmluZw');
 *
 * // expected result
 * ['view_id', 'test string']
 */
export const decode = (value) => atob(decodeURIComponent(value)).split('+');

/**
 * Convert string condensed with [`encodeID`]{@link module:core/util.encodeID} back into path-like ID value.
 *
 * @method
 *
 * @since 3.3.1
 *
 * @param {string} id - String which has been condensed with `encodeID`.
 *
 * @return {string}
 *
 * @see [`lettersToNumber`]{@link module:core/util.lettersToNumber}
 *
 * @example
 * // condensed ID value
 * let id = 'srt-Yi1idS1idg';
 *
 * decodeID(id);
 *
 * // result:
 * 'settings-images-background'
 */
export const decodeID = (id) => decode(id.replace('srt-', ''))[0].split('-').map(v => {
  // if value is an number, return as is
  if (!Number.isNaN(+v)) return v;
  // otherwise return decoded value
  return config.id_index[lettersToNumber(v)];
}).join('-');

/**
 * Encode string(s) into Base64 encoded string. If multiple strings are provided, they are encoded as a single string joined by "+".
 *
 * *Note that byte padding "=" signs are stripped.*
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {...string|number} values - String(s) to encode into Base64.
 *
 * @return {string}
 *
 * @example
 * encode('view_id', 'test string');
 *
 * // expected result (encoded "view_id+test string")
 * 'dmlld19pZCt0ZXN0IHN0cmluZw'
 */
export const encode = (...values) => btoa(values.join('+')).replace(/=/g, '');

/**
 * Convert path-like ID value into a condensed string which is reversible with [`decodeID`]{@link module:core/util.decodeID}.
 *
 * @method
 *
 * @since 3.3.1
 *
 * @param {string} id - Path-like ID value.
 *
 * @return {string}
 *
 * @see [`numberToLetters`]{@link module:core/util.numberToLetters}
 *
 * @example
 * // original ID value
 * let id = 'settings-images-background';
 *
 * encodeID(id);
 *
 * // result:
 * 'srt-Yi1idS1idg'
 */
export const encodeID = (id) => `srt-${encode(id.split('-').map(p => {
  // if property is an array index, return as is
  if (!Number.isNaN(+p)) return p;
  // get index of property in ID index
  let i = config.id_index.indexOf(p);
  // add property to ID index if it does not exist
  if (i === -1) i = config.id_index.push(p) - 1;
  return numberToLetters(i);
}).join('-'))}`;

/**
 * Create expanded attributes from a condensed attr object.
 *
 * This method is essentially the reverse of [`condenseAttributes`]{@link module:core/util.condenseAttributes}, creating an array of individual attribute objects based on the provided attribute object. An optional labels object can be provided, which will add labels to the attribute objects. Complex attributes will be given a block number based on their parent property location.
 *
 * *Note that this method only supports single-level complex attributes at this time (one child property).*
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {object} attrObj - Condensed attribute object to expand.
 *
 * @param {object} [labels] - Object containing attribute names and their labels. When provided, any attribute object created will also include a `label` property.
 *
 * @param {object} [args] - Attribute type definition.
 *
 * @return {array}
 *
 * @example
 * // create attribute objects from condensed attributes
 * let attr = {
 *   processor: 'i7',
 *   ram: 16,
 *   ports: {
 *     hdmi: 1,
 *     thunderbolt_4: 3,
 *   },
 * };
 *
 * let labels = {
 *   processor: 'Intel® Core™ i7',
 *   ram: '16 GB',
 * };
 *
 * expandAttr(attr, labels);
 *
 * // expected result:
 * [
 *   {
 *     name: 'processor',
 *     value: 'i7',
 *     label: 'Intel® Core™ i7',
 *   },
 *   {
 *     name: 'ram',
 *     value: 16,
 *     label: '16 GB',
 *   },
 *   {
 *     name: 'ports',
 *     value: 'thunderbolt_4',
 *     block: 0,
 *   },
 *   {
 *     name: 'ports:count',
 *     value: 3',
 *     block: 0,
 *   },
 *   {
 *     name: 'ports',
 *     value: 'hdmi',
 *     block: 1,
 *   },
 *   {
 *     name: 'ports:count',
 *     value: 1',
 *     block: 1,
 *   },
 * ]
 */
export const expandAttr = (attrObj, labels = {}, args = {}) => {
  // product attributes array
  let attributes = [];

  let makeObject = (name, value, attrPath, block) => {
    // create base attribute object
    let attribute = {name, value};
    // add label for current attribute path, if available
    if (labels[attrPath]) attribute.label = labels[attrPath];
    // add block number, if provided
    if (block !== false) attribute.block = block;
    // add attribute to array
    attributes.push(attribute);
  };

  // function to recursively make attribute object for each attrObj property and value
  let processAttr = (name, value, path = [], propertyName = name, block = false) => {
    let isValueObj = typeof value === 'object' && !Array.isArray(value);
    // copy path
    path = path.slice();
    // add current name to path
    path.push(propertyName);
    // create path string
    let attrPath = path.join('.');

    // exclude non-filterable object values
    let exclude = ['score_detail'];
    if (isValueObj && !exclude.includes(name)) {
      // get object child def name
      let child = getSchemaValue('child', name, args);
      // get object property order
      let attrOrder = getSchemaValue('properties', name, args);
      // define value entries
      let entries = Object.entries(value);
      // if we have a child attribute order, use it to sort entries
      if (attrOrder) entries.sort((a, b) => attrOrder.indexOf(a[0]) - attrOrder.indexOf(b[0]));
      // recurse to next level
      for (let i = 0; i < entries.length; i++) {
        let [n, v] = entries[i];
        makeObject(name, n, attrPath, i);
        processAttr(child, v, path, n, i);
      }
    }
    else makeObject(name, value, attrPath, block);
  };

  // create attributes from product attributes
  for (let [name, value] of Object.entries(attrObj)) processAttr(name, value);
  // create attributes from any remaining labels not found in product attributes
  for (let [name, label] of Object.entries(labels)) {
    if (!attrObj[name]) attributes.push({name, label});
  }

  return attributes;
};

/**
 * Format attributes values. Takes an arbitrary attributes object which may have incorrect types, and casts them to the correct type, as defined in its [schema]{@link module:core/config.schema} `type`.
 *
 * If no schema is defined for the attribute type, the attributes are returned unchanged.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} attributes - Object containing filter attributes and their values.
 *
 * @param {object} [args] - Attribute definition object.
 *
 * @param {string} [args.type] - Attribute type. Default: `filter`
 *
 * @param {boolean} [args.allowUnrecognized] - When enabled, all values will be returned, even when a schema definition or type is not provided. Default: `false`
 *
 * @return {object}
 *
 * @example
 * let attributes = {
 *   form_factor: "Notebook", // should be lowercase
 *   price: "1599.99", // should be type "number"
 *   ram: [["100", "250"], ["250", "500"]], // array of "number" types
 *   touchscreen: "true", // should be type "boolean"
 * }
 *
 * formatAttributes(attributes);
 *
 * // expected output:
 * {
 *   form_factor: "notebook",
 *   price: 1599.99,
 *   ram: [[100, 250], [250, 500]],
 *   touchscreen: true,
 * }
 */
export const formatAttributes = (attributes, args) => {
  // merge args into default options
  let options = Object.assign({
    type: 'filter',
    allowUnrecognized: false,
  }, args);

  // if no schema exists, return unchanged
  if (!(App.settings[options.type] && App.settings[options.type].schema)) return attributes;

  let formatted = {};
  // ensure filter options are the correct types
  for (let name of Object.keys(attributes)) {
    // get attribute type
    let type = getSchemaValue('type', name, options);
    // if attribute type is not defined, and allowUnrecognized is not enabled, remove it
    if (!type && !options.allowUnrecognized) {
      App.debug(3, `Attribute "${name}" in ${options.type} attribute type is not defined in schema. Removing attribute.`);
      delete attributes[name];
    }
    // otherwise set to appropriate type
    else {
      // if string, check if uppercase is allowed
      let allow_uppercase = (type === 'string') ? getSchemaValue('allow_uppercase', name, options) : false;
      formatted[name] = formatValues(attributes[name], type, allow_uppercase);
    }
  }

  return formatted;
};

/**
 * Format attribute values into a specified type. Processes arrays of values recursively.
 *
 * Values may be returned untouched if they are boolean or already the correct type (and no additional processing is needed).
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {*} value - Attribute values to be formatted.
 *
 * @param {string} type - Type definition to format values to. Supported values:
 *
 * - `string` - Type cast to `string`, as lowercase.
 * - `boolean` - Parse string "true"/"false" or number "0"/"1" values into `boolean`.
 * - `number` - Parse string or other values into `number` type.
 *
 * @param {boolean} [allow_uppercase] - When enabled, attribute values will not be forced into lowercase.
 *
 * @return {*}
 *
 * @example
 * // string
 * let value = "Notebook";
 *
 * formatValues(value, 'string');
 *
 * // formatted value:
 * "notebook"
 *
 * @example
 * // boolean
 * let values = [1, "false", "0", "something"];
 *
 * formatValues(values, 'boolean');
 *
 * // formatted values:
 * [true, false, false, true]
 *
 * @example
 * // number
 * let values = [["100", "250"], ["250", "500"]];
 *
 * formatValues(values, 'number');
 *
 * // formatted values:
 * [[100, 250], [250, 500]]
 */
export const formatValues = (value, type, allow_uppercase = false) => {
  // if value type is boolean, return value as is
  if (typeof value === 'boolean') return value;

  // format arrays recursively
  if (Array.isArray(value)) return value.map(value => formatValues(value, type, allow_uppercase));

  // otherwise return the formatted value
  else if (typeof value !== 'object') {

    // return formatted values
    switch(type) {

      case 'string': {
        let string = typeof value === 'string' ? value : String(value);
        return allow_uppercase ? string : string.toLowerCase();
      }

      case 'boolean': {
        // if already boolean, just return it
        if (typeof value === 'boolean') return value;
        // if property is passed as a string "false" or "0", return a boolean false
        if (value === "false" || value === "0") return false;
        // if property is a number representation of boolean, type cast as boolean
        if (typeof value === 'number' && (value === 0 || value === 1)) return Boolean(value);
        // otherwise return boolean of property
        return !!value;
      }

      case 'number': {
        // if already number, just return it
        if (typeof value === 'number') return value;
        // parse string into integer
        if (typeof value === 'string') return parseFloat(value);
        // cast other values to a number
        return Number(value);
      }

      default:
        return value;
    }
  }

  return value;
}

/**
 * Filter an array of objects to only include objects that contain a given property. Returns a new array containing matched objects.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} array - Array of objects.
 * @param {string} property - Property that objects must contain in order to be included in results.
 * @param {boolean} truthy - When enabled, only include the object if the value is truthy. If this parameter is not enabled, objects will be included if they contain this property, even if falsey. Default: `false`
 *
 * @return {array}
 *
 * @example
 * let array = [
 *   { a: 'string', b: 0 },
 *   { b: 4, c: 'other' },
 * ]
 *
 * filterObjectArray(array, 'b');
 *
 * // expected result:
 * [
 *   { a: 'string', b: 0 },
 *   { b: 4, c: 'other' },
 * ]
 *
 * filterObjectArray(array, 'b', true);
 *
 * // expected result:
 * [
 *   { b: 4, c: 'other' },
 * ]
 */
export const filterObjectArray = (array, property, truthy = false) => {
  return array.filter(obj => truthy ? !!obj[property] : obj.hasOwnProperty(property));
};

/**
 * Format a number into a localized string using JavaScript's built-in [Intl.NumberFormat()]{@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat}. More information on number format configuration can be found in the `format_number` [app setting]{@link module:core/config.app_settings}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {number} number - Number representing product price to be formatted to a string.
 *
 * @param {object} options - Formatting options. Accepts any options available in the [Intl.NumberFormat()]{@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat} method. Default: `{}`
 *
 * @return {string|number}
 *
 * @example
 * // config JSON example
 * "format_number": {
 *   "enabled": true,
 *   "locale": "en-US"
 * }
 *
 * @example
 * let price = 149.9
 *
 * let options = {
 *   "style": "currency",
 *   "currency": "USD"
 * }
 *
 * formatNumber(price, options);
 *
 * // expected result:
 * "$149.90"
 */
export const formatNumber = (number, options = {}) => {
  // get format settings
  let format = App.settings.format_number;
  // if format is not enabled, return number unchanged
  if (!format.enabled) return number;
  // merge app format settings with provided options
  options = Object.assign({}, format.options, options);
  // create formatted number
  return new Intl.NumberFormat(format.locale, options).format(number);
};

/**
 * Format a number into a string with appropriate byte units.
 *
 * Values under 1000 will be formatted with GB label, over 1000 will be formatted with TB label. Additionally, values are [formatted]{@link module:core/util.formatNumber} in case any decimals are present in the final value, e.g. when converting gigabytes into terabytes.
 *
 * @method
 *
 * @since 3.4.3
 *
 * @param {number} value - Number to be formatted.
 *
 * @param {string} [template] - Optional template for string formatting. If provided, template string should be contain the tags `[amount]` and `[unit]` to be replaced with value and byte unit, respectively.
 *
 * @return {string}
 *
 * @example
 * let ram = 16;
 *
 * formatBytes(ram);
 *
 * // expected output
 * "16GB"
 *
 * @example
 * let hdd = 2000;
 * let template = '[amount] [unit]';
 *
 * formatBytes(hdd, template);
 *
 * // expected output
 * "2 TB"
 */
export const formatBytes = (value, template = '') => {
  // define attributes labels, if they exist
  let attrLabels = App.settings.labels.attributes || {};
  // get storage labels
  let gb = (attrLabels.storage && attrLabels.storage['gb']) || 'GB';
  let tb = (attrLabels.storage && attrLabels.storage['tb']) || 'TB';
  // get value and label in either GB or TB
  let unit = value >= 1000 ? tb : gb;
  if (value >= 1000) value = round(value, -2) / 1000;
  // format value, if necessary
  value = formatNumber(value);
  // if a template is provided, replace values, otherwise return formed string
  return template ? template.replace('[amount]', `${value}`).replace('[unit]', unit) : `${value}${unit}`;
};

/**
 * Compile "path" strings based on complex attribute values from a provided attribute source. The returned strings can be used to retrieve data from product attributes object using [`getObjectValue`]{@link module:core/util.getObjectValue}. Simple attributes are returned as-is, but wrapped in an array.
 *
 * 1. Provided `attribute` name is split into an array using the complex attribute delimiting character `:`. If attribute is not complex (array length is 1) the attribute name is returned as a single term array and the method stops here.
 * 1. Attributes object is filtered into an array of valid attribute entries, each with the attribute name and values from the relevant attribute. Valid attributes are:
 *     a. Attributes which contain the base term.
 *     b. Attributes which have fewer terms than our requested attribute terms. This is so we can verify, later, that the paths are valid for the attribute based on total terms.
 * 1. Valid entry values are placed into an array (alongside the base attribute term) in the correct position according to their attribute name length. This orders the values so attribute paths can be generated.
 * 1. Values are [cleaned]{@link module:core/util.arrayClean} to remove any undefined values (i.e. if a term in the complex attribute was not selected). If, after cleaning, the values array is shorter than the original attribute term array, then there are no valid paths and an empty array is returned.
 * 1. Since values array may contain multiple values for each complex attribute term, [all combinations of terms are generated]{@link module:core/util.cartesianProduct}. If multiple paths exist, their path terms are joined and the array of valid paths are returned.
 *
 * @method
 *
 * @since 3.1.0
 *
 * @param {string} attribute - Attribute name.
 * @param {object} attrObject - Source for complex attribute values.
 *
 * @return {array}
 *
 * @example
 * // attributes object, e.g. filter request attributes
 * let attr = {
 *   form_factor: ['notebook'],
 *   game: ['csgo', 'valorant'],
 *   game:quality: ['1440p_ultra'],
 *   game:quality:fps: [[60, 120]],
 * };
 *
 * // construct attribute path
 * let paths = getAttributePaths('game:quality:fps', attr);
 *
 * // expected result
 * [
 *   'game.csgo.1440p_ultra',
 *   'game.valorant.1440p_ultra',
 * ]
 */
export const getAttributePaths = (attribute, attrObject) => {
  // if attribute is already a path, return it as is
  if (attribute.includes('.')) return [attribute];
  // split attribute into terms
  let attrTerms = attribute.split(':');
  // if simple attribute, return it
  if (attrTerms.length === 1) return attrTerms;
  // get attribute base term
  let base = attrTerms[0];
  // gather all valid attribute entries necessary to build the complex attribute path
  let entries = Object.entries(attrObject).filter(([objAttr]) => objAttr.split(':')[0] === base && objAttr.split(':').length < attrTerms.length);
  // create values array
  let values = [[base]];
  // place values in array at their correct location
  for (let [objAttr, value] of entries) values[objAttr.split(':').length] = value;
  // clean any missing array values
  values = arrayClean(values);
  // if values array length is shorter than OG attribute path, not all attributes were present, return empty array
  if (values.length < attrTerms.length) return [];
  // get path elements
  let paths = cartesianProduct(...values);
  // combine path names into string
  paths = paths.map(elements => elements.join('.'));

  return paths;
};

/**
 * Determine if a provided value is a file Blob, and return an object URL for it if so. Otherwise, value is returned untouched.
 *
 * @method
 *
 * @since 3.2.4
 *
 * @param {object|string} image - File Blob object or path string.
 *
 * @return {string}
 */
export const getFileURL = (image) => (image instanceof Blob) ? URL.createObjectURL(image) : image;

/**
 * Modify an image URL to use Intel image service API.
 *
 * If a product is using a non-local image source, and the `api_images` [setting]{@link module:core/config.settings} is enabled, the image URL will be formatted into a new URL for use with the Intel image service API. If these conditions are not met, the url is returned unchanged.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {string} url - Image URL to prepare for use with image service API.
 *
 * @see [`isImageAPIURL`]{@link module:core/util.isImageAPIURL}
 * @see Used by:
 * @see [**`lintProducts`**]{@link module:core/filter~lintProducts}
 *
 * @example
 * // config with api images enabled
 * let config = {
 *   api_images: {
 *     enabled: true,
 *     dimensions: [480, 320],
 *   },
 * }
 *
 * // original url
 * let url = 'https://example.com/link-to-product-image.jpg';
 *
 * // run method
 * getImageAPIURL(url);
 *
 * // result
 * 'https://pim-media.intel.com/pub-api/v1/imageservice/customize?url=https://example.com/link-to-product-image.jpg&width=480&height=320'
 * }
 */
export const getImageAPIURL = (url) => {
  let api = App.settings.api_images;
  // stop here if:
  // if API images are not enabled
  // image url is not remote
  // image url has already been uri-zed
  if (!api.enabled || !url.includes('//') || isImageAPIURL(url)) return url;
  // update product image url with image service address and dimensions
  return `https://pim-media.intel.com/pub-api/v1/imageservice/customize?url=${url}&width=${api.dimensions[0]}&height=${api.dimensions[1]}`;
};

/**
 * Get value from object using a provided "path".
 *
 * Returns either the value at the provided path, or the containing object(s) plus the value, depending on the `includeParentObj` param.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} obj - Object to get value from.
 *
 * @param {string|array} path - Path to the desired value. Path can be provided as an array (e.g. `['property', 'child']`), or as a string with a separator character between each location.
 *
 * @param {string} [split] - Optional separator character to split string-based paths with. Default: `'-'`
 *
 * @param {boolean} [includeParentObj] - When enabled, the value is returned inside of the "parent" context, i.e. all containing objects and/or arrays along the provided path.
 *
 * @return {*}
 *
 * @example
 * const object = {
 *   property: {
 *     child: 'Target value',
 *   },
 *   array: [
 *     {
 *       property_2: 100,
 *     },
 *   ],
 * }
 *
 * // get only value
 * getObjectValue(object, 'property-child'); // 'Target value'
 * getObjectValue(object, ['array', '0', 'property_2']); // 100
 *
 * // get parent context
 * getObjectValue(object, 'property.child', '.', true);
 * // expected output:
 * {
 *   property: {
 *     child: 'Target value',
 *   }
 * }
 */
export const getObjectValue = (obj, path, split = '-', includeParentObj = false) => {
  // if path is not an array, create one
  if (!Array.isArray(path)) path = path.split(split);
  // loop through path items to get to value
  for (let i = 0, len = path.length; i < len; i++) {
    obj = obj[path[i]];
    if (obj === undefined) return;
  }
  // return value
  return includeParentObj ? putObjectValue(obj, {}, path) : obj;
};

/**
 * Get a random integer between two provided values.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {number} min - Lower bound
 * @param {number} max - Upper bound
 *
 * @returns {number}
 *
 * @example
 * let numbers = [];
 *
 * // get three random numbers
 * for (let i = 0; i < 3; i++) numbers.push(getRandomInt(1, 10));
 *
 * // example result
 * [7, 10, 1]
 */
export const getRandomInt = (min,max) => Math.floor(Math.random() * Math.floor(max - min + 1)) + min;

/**
 * Get attribute schema property value, for a provided attribute name.
 *
 * If a path-like complex attribute name is provided (e.g. `storage.ssd`), the property value of the final term in the path will be returned. *Note that all attributes must be correctly defined in their respective schema for this functionality to work.*
 *
 * Returns the requested schema property value if found, otherwise `false`.
 *
 * @method
 *
 * @since 3.1.0
 *
 * @param {string} property - Schema property to retrieve a value for.
 * @param {string} attribute - Schema attribute name.
 * @param {object} [args] - Attribute type definition.
 * @param {string} [args.type] - Attribute type. Determines which schema will be used.
 *
 * @return {*}
 *
 * @example
 * // simple attribute example
 *
 * // assume default attribute type
 * let schema = {
 *   ram: {
 *     type: 'number',
 *     compare: 'min',
 *   }
 * };
 *
 * // get ram "compare" value
 * getSchemaValue('compare', 'ram');
 *
 * // expected result
 * 'min'
 *
 * @example
 * // complex attribute path example
 *
 * // assume default attribute type
 * let schema = {
 *   storage: {
 *     type: 'object',
 *     child: 'storage:amount',
 *     properties: [ 'ssd', 'hdd' ],
 *   },
 *   'storage:amount': {
 *     type: 'number',
 *     compare: 'min',
 *   }
 * };
 *
 * // get ram "compare" value
 * getSchemaValue('type', 'storage.ssd');
 *
 * // expected result
 * 'number'
 */
export const getSchemaValue = (property, attribute, args = {}) => {
  // merge args into default options
  let options = Object.assign({
    type: 'filter',
  }, args);

  // get schema for requested attribute type
  let schema = App.settings[options.type] && App.settings[options.type].schema;
  // if no schema exists, return false
  if (!schema) return false;
  // if attribute name is a path, determine final term's schema def name
  let terms = attribute.split('.');
  if (terms.length > 1) {
    let termDepth = terms.length - 1;
    attribute = terms[0];
    for (let i = 0;  i < termDepth; i++) {
      if (!schema[attribute]) return false;
      attribute = schema[attribute].child;
    }
  }
  // if no schema exists, or attribute name is not in schema, return false
  if (!schema || !(schema[attribute] || schema.global)) return false;
  // initialize def
  let def = schema[attribute] || schema.global;
  // return value or false
  return def[property] || false;
};

/**
 * Get url querystring parameters. Retrieves either a single parameter value, if provided a key, or an object containing all parameters, if no key is provided.
 *
 * Note that parameter values in comma-separated strings will be split into arrays.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} [key] - Name of querystring parameter to retrieve. If no `key` is provided, parameter object is returned. Default: `''`
 *
 * @param {string} [url] - Manually define a string to check for querystring parameters. If one is not provided, the browser Location interface is used. Default: `''`
 *
 * @returns {*|object}
 *
 * @example
 * // example querystring
 * '?processor=i5&storage_amount=250,499'
 *
 * // get specific param key
 * getURLParam('processor');
 *
 * // expected result:
 * 'i5'
 *
 * // get params object
 * getURLParam();
 *
 * // expected result:
 * {
 *   processor: 'i5',
 *   storage_amount: [250, 499]
 * }
 */
export const getURLParam = (key = '', url = '') => {
  let qs = url ? url.split('?')[1] || '' : location.search.substring(1);
  // if no querystring exists, return false
  if (!qs.length) return false;
  // isolate query elements, after replacing any "+" signs with spaces
  const query = qs.replace(/\+/g, ' ').split('&');
  // build parameters object
  let params = {};
  for (let q of query) {
    // split param into a key and value
    let p = q.split('=');
    // if param value is an array (includes commas), split the value into an array, then add to object
    params[p[0]] = p[1].split(',').length === 1 ? p[1] : p[1].split(',');
  }
  // return param value by key, if provided. otherwise return param object
  return key ? params[key] : params;
};

/**
 * Filter an array of objects by a key:value pair. Returns an array of indices for objects matching the provided criteria, or the index of the first object found. If no matches are found, returns `-1`.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} property - Name of property to match.
 * @param {*} value - Value of property to match. Must be a primitive.
 * @param {array} objArray - The array of objects to search.
 * @param {number} [fromIndex] - When enabled, returns an array of indices, otherwise returns the first matched object index. Default: `false`
 *
 * @return {number}
 *
 * @example
 * let array = [
 *   { a: 1, b: 2, c: 3 },
 *   { a: 3, b: 5, c: 3 },
 *   { a: 1, b: 6, c: 3 },
 * ]
 *
 * indexOfObjectByProperty('a', 1, array);
 * // expected result:
 * 0
 *
 * indexOfObjectByProperty('a', 1, array, 1);
 * // expected result:
 * 2
 */
export const indexOfObjectByProperty = (property, value, objArray, fromIndex = 0) => {
  // check if array is provided
  if (!Array.isArray(objArray)) throw 'Util.indexOfObjectByProperty requires an array.';
  // return first object with property:value pair
  for (let i = fromIndex; i < objArray.length; i++) {
    let obj = objArray[i];
    // element must be an object
    if (typeof obj !== 'object') throw 'Util.indexOfObjectByProperty array element is not an object.';
    // if match, return index
    if (obj[property] === value) return i;
  }

  // if no matches found, return -1
  return -1;
};

/**
 * Check if a provided value is within a value range. Criteria to be within range:
 *
 * 1. Min/max values must constitute a [valid range]{@link module:core/util.isRange}.
 * 2. Type of `value` be `number`.
 * 3. Value of `min` or `max` should not equal `value` if `limit` is set to `min`/`max`, respectively.
 * 4. Provided `value` must be greater than or equal to `min` and less than or equal to `max`.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {number|array} value - Value(s) to test. If array of values is provided, all values must be within range.
 * @param {array} range - Valid range array.
 * @param {string} limit - Prevent the value from being equal to a lower or upper bound ("min" or "max", respectively). E.g. if "min" is used, the value must be greater than the lower bound to get a positive result. If no value is provided, `value` will be considered "in range" even if equal to upper or lower bounds.
 *
 * @return {boolean}
 *
 * @example
 * inRange(1, [1, 10], 'min'); // false
 * inRange(7, [1, 10], 'min'); // true
 * inRange(10, [1, 10]); // true
 * inRange(10, [1, 10], 'max'); // false
 */
export const inRange = (value, range, limit = '') => {
  // value is an array, test each value and return result
  if (Array.isArray(value)) return value.reduce((isInRange, value) => inRange(value, range) ? isInRange : false, true);
  // value must be number, min/max must be a valid range
  if (!isRange(range) || typeof value !== 'number') return false;
  // create range object
  const r = {min: range[0], max: range[1]};
  // check if value equals a limit
  const isLimit = value === r[limit];
  // check if in range
  const isInRange = (value >= r.min && value <= r.max);
  return isLimit ? false : isInRange;
};

/**
 * Determine if an attribute name is a complex attribute, i.e. part of an object-type attribute. Tests attribute name for presence of delimiter character `:`.
 *
 * @method
 *
 * @since 3.1.0
 *
 * @param {string} attribute - Attribute name.
 *
 * @return {boolean}
 *
 * @example
 * isAttrComplex('complex_property:child') // true
 * isAttrComplex('complex_property') // false
 */
export const isAttrComplex = (attribute) => attribute.includes(':');

/**
 * Determine if an attribute name is a complex attribute root, i.e. the base term of an object-type attribute.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {string} attribute - Attribute name.
 *
 * @param {object} [args] - Attribute type definition.
 *
 * @param {string} [args.type] - Attribute type. Determines which schema will be used.
 *
 * @return {boolean}
 *
 * @example
 * isAttrComplexRoot('simple_property') // false
 * isAttrComplexRoot('complex_property') // true
 * isAttrComplexRoot('complex_property:child') // false
 */
export const isAttrComplexRoot = (attribute, args = {}) => !isAttrComplex(attribute) && getSchemaValue('child', attribute, args);

/**
 * Determine if a provided URL is using the Intel image service API.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {string} url - The URL string to test.
 *
 * @example
 * let url1 = 'https://example.com/link-to-product-image.jpg';
 * let url2 = 'https://pim-media.intel.com/pub-api/v1/imageservice/customize?url=https://example.com/link-to-product-image.jpg&width=480&height=320';
 *
 * // test
 * getImageAPIURL(url1); // false
 * getImageAPIURL(url2); // true
 */
export const isImageAPIURL = (url) => url.includes('pim-media.intel.com')

/**
 * Test if a value is a number. To evaluate as `true`, the `value` must meet at least one following conditions:
 *
 * - type is `number`
 * - value is not `false` when testing with `Number.isNaN`
 *
 * @method
 *
 * @since 3.3.2
 *
 * @param {*} value - Value to test.
 *
 * @return {boolean}
 *
 * @example
 * isNum(12); // true
 * isNum(12.1); // true
 * isNum('12'); // true
 * isNum('12.1'); // true
 * isNum('.12.1'); // false
 */
export const isNum = (value) => typeof value === 'number' || !Number.isNaN(+value);

/**
 * Test if a string contains only digits. If a string is not provided, it will be converted to a string before testing.
 *
 * @method
 *
 * @since 3.3.2
 *
 * @param {*} value
 *
 * @return {boolean}
 *
 * @example
 * isDigits(12); // true
 * isDigits(12.1); // false
 * isDigits('12'); // true
 * isDigits('12.1'); // false
 */
export const isDigits = (value) => /^\d+$/.test(`${value}`);

/**
 * Test if array resembles a value range. Criteria for a range:
 *
 * 1. Item must be an array
 * 2. Array length must be 2
 * 3. Both array values must be of type `number`
 * 4. Array index `0` must be less than array index `1`
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} array - Array to test. Must contain two values, both of type number, where the first is smaller than the second.
 *
 * @return {boolean}
 *
 * @example
 * isRange([0, 250, 500]); // false
 * isRange(['250', '500']); // false
 * isRange([500, 250]); // false
 * isRange([250, 500]); // true
 */
export const isRange = (array) => Array.isArray(array) && array.length === 2 && typeof array[0] === 'number' && typeof array[1] === 'number' && array[0] < array[1];

/**
 * Convert letters into a number using an alpha "26ary" to base 10 conversion. Reverses a string created by [`numberToLetters`]{@link module:core/util.numberToLetters}.
 *
 * *Note that any uppercase letters will be converted to lowercase before processing.*
 *
 * @method
 *
 * @since 3.3.1
 *
 * @param {string} letters - String containing alpha characters.
 *
 * @return {number}
 *
 * @see Used by:
 * @see [**`decodeID`**]{@link module:core/util.decodeID}
 *
 * @example
 * lettersToNumbers('a'); // 0
 * lettersToNumbers('bb'); // 53
 * lettersToNumbers('iii'); // 6326
 */
export const lettersToNumber = (letters) => letters.toLowerCase().split('').reverse().reduce((n, l, i) => n + (Math.pow(26, i) * (config.alpha.indexOf(l) + 1)), 0) - 1;

/**
 * Evaluate the log base [x] value for a provided [y].
 *
 * @method
 *
 * @param {number} x - Log base to evaluate with.
 *
 * @param {number} y - Value to evaluate log base of.
 *
 * @returns {number}
 *
 * @example
 * logBase(2, 8) // 3
 * logBase(10, 20) // 2
 * logBase(5, 625) // 4
 */
export const logBase = (x, y) => Math.log(y) / Math.log(x);

/**
 * Log caught error, plus an optional error message highlighted in red.
 *
 * @method
 *
 * @since 3.2.4
 *
 * @param error - Error to log.
 * @param {string} errorMessage - Message to accompany error.
 */
export const logError = (error, errorMessage = '') => {
  // display custom message, if provided
  if (errorMessage !== '') console.log(`%c${errorMessage}`, 'font-weight:bold; color:red;');
  console.log(error);
};

/**
 * Simplify promise usage. Takes a `callback` function to run once promise is fulfilled, a `handler` function which resolves/rejects the promise, and a message to display if the promise is rejected.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {function} callback - Do something once promise is fulfilled.
 * @param {function} handler - Function to wait for. Must be able to resolve/reject promises.
 * @param {string} [errorMessage] - Text or other data to display on caught errors.
 */
export const makePromiseTo = (callback, handler, errorMessage = '') => {
  // format display errors
  const displayErrors = (error) => {
    // display custom message, if provided
    if (errorMessage !== '') console.log(`%c${errorMessage}`, 'font-weight:bold; color:red;');
    console.log(error);
  };

  new Promise((resolve, reject) => handler(resolve, reject)).then(callback).catch((error) => displayErrors(error));
};

/**
 * Merge objects. Arrays are replaced. Uses [lodash's mergeWith]{@link https://lodash.com/docs/#mergeWith}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} object - The destination object.
 * @param {...object} sources - Source objects.
 *
 * @return {object}
 *
 * @example
 * let object1 = {
 *   a: { i: 'i', ii: 'ii' },
 *   b: [ 1, 2, 3 ],
 * };
 *
 * let object2 = {
 *   a: { iii: 'iii' },
 *   b: [ 4, 5 ],
 * };
 *
 * merge(object1, object2);
 * // expected result:
 * {
 *   a: { i: 'i', ii: 'ii', iii: 'iii' },
 *   b: [ 4, 5 ],
 * }
 */
export const merge = (object, ...sources) => {
  const customizer = (objValue, srcValue) => {
    if (Array.isArray(objValue)) return srcValue;
  }

  return _mergeWith(object, ...sources, customizer);
};

/**
 * Merge objects and concatenate arrays. Uses [lodash's mergeWith]{@link https://lodash.com/docs/#mergeWith}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} object - The destination object.
 * @param {...object} sources - Source objects.
 *
 * @return {object}
 *
 * @example
 * let object1 = {
 *   a: { i: 'i', ii: 'ii' },
 *   b: [ 1, 2, 3, 4, 5 ],
 * };
 *
 * let object2 = {
 *   a: { iii: 'iii' },
 *   b: [ 4, 5 ],
 * };
 *
 * merge(object1, object2);
 * // expected result:
 * {
 *   a: { i: 'i', ii: 'ii', iii: 'iii' },
 *   b: [ 1, 2, 3, 4, 5, 4, 5 ],
 * }
 */
export const mergeAll = (object, ...sources) => {
  const customizer = (objValue, srcValue) => {
    if (Array.isArray(objValue)) return objValue.concat(srcValue);
  }

  return _mergeWith(object, ...sources, customizer);
};

/**
 * Merge objects and arrays, keeping only unique values in arrays. Uses [lodash's mergeWith]{@link https://lodash.com/docs/#mergeWith}.
 *
 * @method
 *
 * @since 3.4.2
 *
 * @param {object} object - The destination object.
 * @param {...object} sources - Source objects.
 *
 * @return {object}
 *
 * @example
 * let object1 = {
 *   a: { i: 'i', ii: 'ii' },
 *   b: [ 1, 2, 3, 4, 5 ],
 * };
 *
 * let object2 = {
 *   a: { iii: 'iii' },
 *   b: [ 4, 5 ],
 * };
 *
 * merge(object1, object2);
 * // expected result:
 * {
 *   a: { i: 'i', ii: 'ii', iii: 'iii' },
 *   b: [ 1, 2, 3, 4, 5 ],
 * }
 */
export const mergeAllUnique = (object, ...sources) => {
  const customizer = (objValue, srcValue) => {
    if (Array.isArray(objValue) && Array.isArray(srcValue)) {
      arrayPushUnique(objValue, ...srcValue);
      return objValue;
    }
  }

  return _mergeWith(object, ...sources, customizer);
};

/**
 * Convert a number into letters using a base 10 to alpha "26ary" conversion. Reverses a number created by [`lettersToNumber`]{@link module:core/util.lettersToNumber}.
 *
 * @method
 *
 * @since 3.3.1
 *
 * @param {number} number - Any positive number.
 *
 * @return {string}
 *
 * @see Used by:
 * @see [**`encodeID`**]{@link module:core/util.encodeID}
 *
 * @example
 * lettersToNumbers(0); // a
 * lettersToNumbers(53); // bb
 * lettersToNumbers(6326); // iii
 */
export const numberToLetters = (number) => {
  let letters = '';
  while (number >= 0) {
    letters = config.alpha[number % 26] + letters;
    number = Math.floor(number / 26) - 1;
  }
  return letters;
}

/**
 * Test a "haystack" object to see if a "needle" object property:value pairs are contained within it. For most consistent results, do not use with nested objects (with the exception of value arrays).
 *
 * When `checkRange` param is enabled, any array values will be treated as possible [number ranges]{@link module:core/util.isRange}. If the haystack value is a valid range, all needle values must be within the range. If the needle value is a range, but the haystack is not an array, the haystack value must be within the range.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} needle - Object with property values to look for.
 * @param {object} haystack - Object that may or may not include desired property values.
 * @param {boolean} [checkRange] - Whether value arrays should be treated as a possible [ranges]{@link module:core/util.isRange}.
 *
 * @returns {boolean}
 *
 * @example
 * let haystack = {
 *   processor: 'i5',
 *   os: 'windows',
 *   storage_amount: [500, 999],
 * };
 *
 * let needle1 = {
 *   processor: 'i5',
 * };
 *
 * let needle2 = {
 *   processor: 'i5',
 *   storage_amount: 500,
 * };
 *
 * let needle3 = {
 *   processor: 'i5',
 *   storage_amount: 750,
 * };
 *
 * objectIncludes(needle1, haystack); // true
 * objectIncludes(needle2, haystack); // true
 * objectIncludes(needle3, haystack); // false
 * objectIncludes(needle3, haystack, true); // true
 */
export const objectIncludes = (needle, haystack, checkRange = false) => {
  // loop through each needle property
  for (let [property, nv] of Object.entries(needle)) {
    // fail if haystack does not have a value for this property
    if (!haystack.hasOwnProperty(property)) return false;

    // get haystack property value
    let hv = haystack[property];
    // are needle & haystack values arrays
    let nvIsArray = Array.isArray(nv);
    let hvIsArray = Array.isArray(hv);

    // if neither value are arrays, values must match
    if (!hvIsArray && !nvIsArray && nv !== hv) return false;

    // if haystack value is an array
    if (hvIsArray) {
      // convert needle value to array, if it isn't already
      nv = nvIsArray ? nv : [nv];
      // are all needle values within haystack ranges
      const cond1 = checkRange && isRange(hv) && nv.reduce((a, v) => inRange(v, hv) ? a : false, true);
      // are all needle values found in haystack
      const cond2 = arrayIncludes(hv, ...nv);
      // fail if both conditions are false
      if (!cond1 && !cond2) return false;
    }

    // if needle value is an array and ranges are allowed, check if haystack value is in needle range. if not, fail
    if (nvIsArray && !hvIsArray && checkRange && isRange(nv) && !inRange(hv, nv)) return false;
  }

  // if there are no fail conditions, return true
  return true;
};

/**
 * Sorts object keys alphabetically, then returns the sorted object.
 *
 * Note that objects are not sorted recursively - only the top level properties are sorted.
 *
 * Returns a new object.
 *
 * @method
 *
 * @since 3.4.2
 *
 * @param {object} object - Object with keys which need to be sorted.
 *
 * @return {object}
 *
 * @example
 * // object to sort
 * let unsorted = {
 *   bravo: 2,
 *   alpha: 1,
 *   charlie: 3,
 * }
 *
 * objectSort(unsorted);
 *
 * // expected result
 * {
 *   alpha: 1,
 *   bravo: 2,
 *   charlie: 3,
 * }
 */
export const objectSort = (object) => {
  if (typeof object !== 'object') return object;
  return Object.keys(object).sort().reduce((a, key) => {
    a[key] = object[key];
    return a;
  }, {});
};

/**
 * Gather an array of data attributes objects of a specific type for a single element. An valid element is technically anything that contains [`attributes`]{@link module:core/config.attributes}, but is typically an [`options`]{@link module:core/config.options} object or HTML element.
 *
 * Return value is an array of uncondensed [`attributes`]{@link module:core/config.attributes} objects of the specified type. Original attribute properties are included with the exception of the `types` property, since the type is provided as a parameter and is therefore known at the time of the function call.
 *
 * If the containing object includes a `label` property, the value of the label is added to the attribute as `label`.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {HTMLElement|object} item - HTML element or object to collect attributes from. If an HTML element is provided, object data for the option is gathered with [`getElementData`]{@link module:core/view.getElementData}.
 *
 * @param {object} [args] - Attribute type definition.
 *
 * @param {string} [args.type] - Name of attribute to retrieve.
 *
 * @return {array}
 *
 * @example
 * // option object
 * let option = {
 *   label: 'Editing photos',
 *   attributes: [
 *     {
 *       types: ['filter', 'analytics'],
 *       name: 'processor',
 *       value: 'i7',
 *       priority: 3,
 *     },
 *     {
 *       types: ['filter', 'analytics'],
 *       name: 'storage_amount',
 *       value: 500,
 *     },
 *     {
 *       types: ['analytics'],
 *       name: 'usage_model',
 *       value: 'editing-photos',
 *     },
 *   ],
 * };
 *
 * // get attributes
 * parseElementAttributes(option, { type: 'filter' });
 *
 * // expected result:
 * [
 *   {
 *     label: 'Editing photos',
 *     name: 'processor',
 *     value: 'i7',
 *     priority: 3,
 *   },
 *   {
 *     label: 'Editing photos',
 *     name: 'storage_amount',
 *     value: 500,
 *   },
 * ]
 */
export const parseElementAttributes = (item, args = {}) => {
  // set default options
  let options = {
    type: App.settings.primary_attribute.type,
  };

  // merge args into options
  options = Object.assign(options, args);

  // get current option data if not already in json format
  if (item instanceof HTMLElement) item = View.getElementData(item);

  // item has no attributes, return empty array
  if (!item.attributes) return [];

  // attributes array
  let attributes = [];

  // get attributes
  for (let attr of item.attributes) {
    // if the attribute does not include our desired attribute type, skip
    if (!attr.types.includes(options.type)) continue;
    // create copy of attribute so it's safe to manipulate
    let attribute = Object.assign({}, attr);
    // if not a field (e.g. if an option) and a label exists, add item label, unless one already exists
    if (!item.hasOwnProperty('field_type') && item.label && !attribute.hasOwnProperty('label')) attribute.label = item.label;
    // remove unnecessary data
    if (attribute.types) delete attribute.types;
    // add attribute to attributes array
    attributes.push(attribute);
  }

  return attributes;
};

/**
 * Parse a string, which includes a number and a unit, into an array of its numerical value and unit, respectively. Unit strings are standardized into a subset of the supported [EMCAScript units]{@link https://tc39.es/proposal-unified-intl-numberformat/section6/locales-currencies-tz_proposed_out.html#sec-issanctionedsimpleunitidentifier}, including:
 *
 * - inch
 * - centimeter
 * - meter
 * - pound
 * - gram
 * - kilogram
 * - hour
 *
 * Additionally, any supported currencies in `schema.price_currencies` [filter config]{@link module:core/filter.config} will be returned.
 *
 * @method
 *
 * @since 3.4.6
 *
 * @param {string} str - String to parse.
 * @param {array} [out] - Array to reuse, if desired.
 *
 * @return {array}
 *
 * @example
 * // example strings, accepts any commonly used abbreviation
 * let values = [
 *   '1.23kg',
 *   '1.23 kilograms',
 *   '13.3"',
 *   '35.6cm',
 *   '2.12 lbs',
 *   '999.99 GBP',
 * ];
 *
 * values.map(str => parseUnit(str));
 *
 * // expected result
 * [
 *   [1.23, 'kilogram'],
 *   [1.23, 'kilogram'],
 *   [13.3, 'inch'],
 *   [35.6, 'centimeter'],
 *   [2.12, 'pound'],
 *   [999.99, 'GBP'],
 * ]
 */
export const parseUnit = (str, out = [0, '']) => {
  let ref = {
    inch: ['"', 'in', 'inches'],
    centimeter: ['cm', 'centimeters'],
    meter: ['m', 'meters'],
    pound: ['lb', 'lbs', 'pounds'],
    gram: ['g', 'grams'],
    kilogram: ['kg', 'kilograms'],
    hour: ['hr', 'hours'],
  };

  str = String(str);
  let unit = str.match(/[\d.,+]*\s*(.*)/)[1] || '';
  // check if unit is a supported currency
  let isCurrency = App.settings.filter.schema.price_currencies.properties.includes(unit);
  // if a unit was found, but it is not a valid key or a currency, attempt to find match in ref object
  if (unit && !ref[unit] && !isCurrency) unit = Object.entries(ref).reduce((u, [key, values]) => values.includes(unit) ? key : u, '');

  out[0] = parseFloat(str);
  out[1] = unit;

  return out;
};

/**
 * Place a value into an object at a given "path".
 *
 * Returns the `obj` with the value merged in. Note that value is merged in place, meaning that using the returned object is not necessary if it is already available to the function using it.
 *
 * @method
 *
 * @since 3.2.4
 *
 * @param {*} value - Value to place into the `obj`.
 *
 * @param {object} obj - Object to place `value` into.
 *
 * @param {string|array} path - Path to the desired `value`. Path can be provided as an array (e.g. `['property', 'child']`), or as a string with a separator character between each location (e.g. `property-child`).
 *
 * @param {string} [split] - Optional separator character to split string-based paths with. Default: `'-'`
 *
 * @return {object|array}
 *
 * @example
 * // target object
 * let obj = {
 *   property: {
 *     child: 'Target value',
 *   }
 * }
 *
 * // update child value
 * putObjectValue('Some other value', obj, 'property.child', '.');
 *
 * // expected result
 * {
 *   property: {
 *     child: 'Some other value',
 *   }
 * }
 */
export const putObjectValue = (value, obj, path, split = '-') => {
  // if path is not an array, create one
  if (!Array.isArray(path)) path = path.split(split);
  // reverse path
  path.reverse();
  // loop through path items to get to value
  for (let i = 0, len = path.length; i < len; i++) {
    // if path key is a number, use array as parent, otherwise use object
    let parent = !isNaN(path[i]) ? [] : {};
    // if path key is a number, parse integer for use with array
    let property = !isNaN(path[i]) ? parseInt(path[i]) : path[i];
    // set value as the property of the new parent
    parent[property] = value;
    // set parent as the new value
    value = parent;
  }
  // merge value into object
  return _merge(obj, value);
};

/**
 * Create url querystring from object.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} data - Object to convert into a querystring.
 *
 * @returns {string}
 *
 * @example
 * let object = {
 *   processor: 'i5',
 *   os: 'windows',
 *   storage_amount: [500, 999],
 * };
 *
 * queryString(object);
 *
 * // expected result:
 * 'processor=i5&os=windows&storage_amount=500,999'
 */
export const queryString = (data) => {
  return Object.keys(data).map(key => `${key}=${data[key]}`).join('&').replace(/ /g, '+');
};

/**
 * Round numbers to a specified decimal precision.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {number} value - The number to round.
 *
 * @param {number} [precision] - The number of decimal places or digits to round the number to. Default: `0`
 *
 * @return {number}
 *
 * @example
 * // round to whole number
 * round(12345.6789) // 12346
 *
 * @example
 * // round to decimal precision
 * round(12345.6789, 1) // 12345.7
 * round(12345.6789, 2) // 12345.68
 *
 * @example
 * // round to nearest 10, 100, etc
 * round(12345.6789, -1) // 12350
 * round(12345.6789, -2) // 12300
 *
 * @example
 * // round negatives
 * round(-123.45, 1) // -123.4
 * round(123.45, 1) // 123.5
 */
export const round = (value, precision) => {
  let multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * Randomize array in-place using [Durstenfeld shuffle algorithm]{@link https://gist.github.com/webbower/8d19b714ded3ec53d1d7ed32b79fdbac}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} array - Array of items to be shuffled.
 * @param {boolean} inPlace - When enabled, array will be shuffled in place, otherwise a new array will be returned. Default: `false`
 *
 * @return {array}
 *
 * @example
 * let array = [1, 2, 3, 4, 5];
 *
 * shuffle(array);
 * shuffle(array);
 * shuffle(array);
 *
 * // example result
 * [3, 2, 1, 5, 4]
 * [3, 4, 5, 2, 1]
 * [2, 3, 1, 5, 4]
 *
 */
export const shuffle = (array, inPlace = false) => {
  const shuffled = inPlace ? array.slice() : array;

  for (let i = shuffled.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    let temp = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = temp;
  }

  return array;
};

/**
 * Compare arrays or objects by using JSON stringify to turn each into a string, then testing their equivalence.
 *
 * **This has some obvious and major limitations, use only when object are low complexity and content order is highly controlled.**
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {*} a - The first item to compare.
 * @param {*} b - The second item to compare.
 *
 * @return {boolean}
 *
 */
export const stringifyCompare = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Measure execution time between two arbitrary points in the JS code.
 *
 * To begin timer, place `timer('start');` at the beginning of the section to be timed. To stop timer and log time elapsed, place `timer('stop');`
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} time - `start` or `end` timer
 */
export const timer = (time) => {
  if (time === 'start') App.settings.timeStart = window.performance.now();

  if (time === 'stop') {
    if (!App.settings.timeStart) {
      console.log('Util.timeFunction(): No start time set');
      return;
    }
    let end = window.performance.now();
    console.log(`Execution time: ${Math.round(end - App.settings.timeStart)} ms`);
    delete App.settings.timeStart;
  }
};

/**
 * Limit line height on a text element, and replaces overrunning text with an ellipsis. Original text value is also stored as a data attribute so it can be undone if necessary, e.g. when screen is resized.
 *
 * Line heights are truncated by capturing the element text, removing it from the element, then re-adding it one character at a time until the height grows. Once the height grows, the line height is incremented until the limit is reached, at which point the last three characters of the output are removed and an ellipsis is added. This final string is then added to the element.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} element - DOM element to limit.
 * @param {number} [lineLimit] - Number of lines to limit height to. Default: `1`
 *
 * @example
 * <!-- before truncation -->
 * <p class='truncate'>
 *   Product title that spans
 *   two lines
 * </p>
 *
 * <!-- after truncation -->
 * <p class='truncate' data-text='Product title that spans two lines'>
 *   Product title that spans...
 * </p>
 */
export const truncateTextLineHeight = (element, lineLimit = 1) => {
  // get text data
  let textData = element.attr('data-text');
  // set working text to textData, or if it doesn't exist, to trimmed element text
  let text = textData ? textData : element.text().trim();
  // if textData wasn't present, add it
  if (!textData) element.attr('data-text', text);

  // create char array
  let chars = text.split('');
  // start output with first char
  let output = chars[0];

  // add first char to element and determine height
  element.text(chars[0]);
  let height = element.height();
  let lineHeight = 1;

  for(let i = 1; i < chars.length; i++) {
    element.text(element.text() + chars[i]);
    // check if height of element has changed
    if (element.height() > height) {
      height = element.height();
      // if element grows, iterate lineHeight
      lineHeight++;
    }
    // if we haven't gone over line limit, add character to output
    if (lineHeight <= lineLimit) output = output + chars[i];
    // otherwise, add ellipsis and break
    else {
      output = output.slice(0, -3) + '&hellip;';
      break;
    }
  }
  // insert output into element
  element.html(output);
};

/**
 * Get URL basename (origin) from a provided URL string.
 *
 * @method
 *
 * @since 3.4.6
 *
 * @param {string} url - URL string to get basename from.
 *
 * @return {string}
 *
 * @example
 * let url = "https://example.com/path/to/page?a=b";
 *
 * urlBasename(url);
 *
 * // expected result:
 * 'https://example.com'
 */
export const urlBasename = (url) => new URL(url).origin;

/**
 * Walks through an object to get to a property specified by a given path, then applies a given function to each step in that path. Useful to collect parent data at arbitrary or unknown depths for a given data location.
 *
 * @method
 *
 * @since 3.4.6
 *
 * @param {Object} obj - The object to walk through.
 *
 * @param {string | Array<string>} path - The path to the desired property. Can be a string separated by split or an array of string path parts.
 *
 * @param {Function} fn - The function to apply to each step in the path. The function takes the arguments `obj` and `result`, which are the object value at the current path step, and the result object, respectively.
 *
 * @param {Object} [result={}] - The initial value of the result object. This value will be modified by the function fn as it's being applied to each step in the path.
 *
 * @param {string} [split='-'] - The string to split path by if it's a string. Default: `-`.
 *
 * @returns {Object} - The result object after it's been modified by fn for each step in the path.
 *
 * @example
 * // data object to walk
 * const data = {
 *   user: {
 *     label: 'User information',
 *     name: 'John Doe',
 *     age: 30,
 *     email: 'johndoe@example.com',
 *     address: {
 *       label: 'Street address',
 *       street: '123 Main St',
 *       city: 'Anytown',
 *       state: 'CA',
 *     },
 *   },
 * };
 * // path to the street address
 * const path = 'user-address-street';
 * //
 * const result = [];
 * // function to capture all `label` properties in the path
 * const fn = (obj, result) => {
 *   if (obj.label) result.push(obj.label);
 *   return result;
 * };
 *
 * const output = walkObjectPath(data, path, fn, result, '-');
 * // expected output: ['User information', 'Street address']
 */
export const walkObjectPath = (obj, path, fn, result = {}, split = '-') => {
  // if path is not an array, create one
  if (!Array.isArray(path)) path = path.split(split);
  // loop through path items to get to value
  for (let i = 0, len = path.length; i < len; i++) {
    // at each level, run function to modify result
    result = fn(obj, result);
    // go to next level
    obj = obj[path[i]];
    if (obj === undefined) return;
  }

  return result;
};
